import {
  ApiAuthRefreshTokenFailedAction,
  ApiAuthRefreshTokenFailedReason,
  ApiAuthRefreshTokenSucceededAction,
  apiAuthRefreshTokenFailedType,
  apiAuthRefreshTokenRequested,
  apiAuthRefreshTokenSucceededType
} from '../../api/auth/refreshApi';
import { delay, put, select } from 'typed-redux-saga';
import createServerTokenSessionSyncAction from '../../session/createServerTokenSessionSyncAction';
import generateActionId from '../../../generateActionId';
import logger from '../../../logger';
import { oneSecondInMilliseconds } from '../../../const';
import selectSessionContent from '../../session/selectors/selectSessionContent';
import waitForSucceededOrFailedAction from './waitForSucceededOrFailedAction';

const minRefreshInSeconds = 90;
const retryInSeconds = 60;

function* refreshTokenSaga(): Generator<unknown, boolean> {
  let refreshSucceededOrRefreshInvalid = false;
  const requestedActionId = generateActionId();

  yield* put(
    apiAuthRefreshTokenRequested({
      actionId: requestedActionId
    })
  );

  const resultAction = yield* waitForSucceededOrFailedAction<
    typeof apiAuthRefreshTokenSucceededType,
    typeof apiAuthRefreshTokenFailedType,
    ApiAuthRefreshTokenSucceededAction,
    ApiAuthRefreshTokenFailedAction
  >(requestedActionId, apiAuthRefreshTokenSucceededType, apiAuthRefreshTokenFailedType);
  if (resultAction.type === 'succeeded') {
    const payload = resultAction.action.payload;
    yield* put(createServerTokenSessionSyncAction(payload.tokens));
    refreshSucceededOrRefreshInvalid = true;
  } else {
    const payload = resultAction.action.payload;
    if (payload.reason === ApiAuthRefreshTokenFailedReason.TOKEN_INVALID) {
      refreshSucceededOrRefreshInvalid = true;
      logger.warn("Refresh token invalid. Won't try to refresh again");
    }
  }

  return refreshSucceededOrRefreshInvalid;
}

function* refreshCsrSessionTokenSaga(): Generator {
  const { tokens } = yield* select(selectSessionContent);
  if (tokens !== null && tokens.refreshToken !== null) {
    const refreshInSeconds =
      tokens.expiresInSeconds > minRefreshInSeconds ? tokens.expiresInSeconds : 0;
    yield* delay(refreshInSeconds * oneSecondInMilliseconds);

    let refreshSucceededOrRefreshInvalid = false;
    do {
      refreshSucceededOrRefreshInvalid = yield* refreshTokenSaga();
      yield* delay(retryInSeconds * oneSecondInMilliseconds);
    } while (!refreshSucceededOrRefreshInvalid);
  }
}

export default refreshCsrSessionTokenSaga;
